import * as Types from '../../declarations'
import { Progress } from '@mantine/core'
import SectionHeader from './components/SectionHeader'

interface Props {
	trip: Types.DB.Models.TripAttributes
}

const Difficulty = ({
	trip: {
		name,
		jsonData: { trekDifficulty, trekOrClimb },
	},
}: Props) => {
	const isTrek = trekOrClimb === 'TREK'

	if (!isTrek || !trekDifficulty) return <></>

	const trekDifficultyCopy: Record<
		string,
		{ label: string; description: string }
	> = {
		distance: {
			label: 'Distance',
			description:
				'The total distance of the trek. Longer treks generally require more endurance and physical fitness.',
		},
		elevationGain: {
			label: 'Elevation gain',
			description:
				'Total elevation gain throughout the trek. Treks with significant altitude changes can be more challenging, especially if they involve steep ascents or descents.',
		},
		terrain: {
			label: 'Terrain & route condition',
			description:
				' Treks that involve rugged or uneven terrain, such as rocky paths, scree slopes, or dense forests, may require more technical skills or physical effort.',
		},
		altitude: {
			label: 'Altitude / Elevation',
			description:
				'Considering the maximum altitude reached during the trek. Higher altitudes can present challenges like thin air, low oxygen levels, and potential altitude sickness.',
		},
		climaticConditions: {
			label: 'Climate & weather',
			description:
				'How tough are the prevailing weather and climate conditions during the trek. Harsh weather, extreme temperatures, or unpredictable conditions can add to the difficulty.',
		},
		technicalSkills: {
			label: 'Technical difficulty',
			description:
				'Some treks may require basic technical skills like river crossings, using ropes, or scrambling over rocks.',
		},
		supportFacilities: {
			label: 'Access to facilities / Remoteness',
			description:
				'Based on the availability of support facilities such as accommodation, food, water sources, and emergency services. Treks with limited infrastructure or remote areas can be more challenging due to self-sufficiency requirements. The less support facilities there are the harder the trek.',
		},
	}

	const trekDifficulties =
		isTrek && !!trekDifficulty?.individualScores
			? Object.entries(trekDifficulty.individualScores).map(([key, value]) => {
					return {
						label: trekDifficultyCopy[key].label,
						description: trekDifficultyCopy[key].description,
						value,
					}
			  })
			: []

	return (
		<div>
			<SectionHeader
				title={`How difficult is ${name}`}
				section={Types.App.DataTypes.FeedbackSection.DIFFICULTY}
			/>
			<p>
				The trek is <strong>{Math.floor(trekDifficulty.average)} / 10</strong>,
				based on the below factors:
			</p>

			<div className="trek-difficulties">
				{trekDifficulties.map((item, key) => (
					<div key={key} className="trek-difficulty">
						<div>
							<strong>
								{item.label} {item.value}&nbsp;/&nbsp;10
							</strong>
							<Progress
								size={'xl'}
								value={item.value * 10}
								color={(() => {
									if (item.value > 6.6) {
										return 'red'
									}
									if (item.value > 3.3) {
										return 'orange'
									}

									return 'green'
								})()}
							/>
						</div>
						<p>{item.description}</p>
					</div>
				))}
			</div>
		</div>
	)
}

export default Difficulty
